import { useContext } from "react";

import { css } from "@emotion/react";
import CircularProgress from "@mui/material/CircularProgress";

import { colors } from "@dotnet/telematics-design-system";

import { ResourceEventData } from "context/signalR/hooks/types/interfaces";
import { useSignalRForTable } from "context/signalR/hooks/useSignalRForTable";
import TableContext from "context/table/tableContext";
import { noop } from "shared/functions";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { useScroll } from "shared/hooks/useScroll";

import TableLoader from "../Loaders/TableLoader";
import TableDetails from "../TableDetails";
import TableEmpty from "../TableEmpty";
import Toolbar from "../Toolbar";
import {
    useTableData,
    useTableDetailHeights,
    useTableRowSignalRActions
} from "./hooks";
import TableDesktop from "./TableDesktop";
import TableMobile from "./TableMobile";
import { TableProps } from "./types";

const Table = ({ customStyle, onScroll = true }: TableProps) => {
    const { blue700 } = colors;

    const {
        hasNoToolbar,
        hasDetails,
        rows,
        isTableLoading,
        tableName,
        isInnerTable,
        idType
    } = useContext(TableContext);

    const { tableSectionRef, detailsHeights, setDefaultHeigth } =
        useTableDetailHeights();

    useTableData();
    const { editRow, handleDeletion } = useTableRowSignalRActions();

    useSignalRForTable(
        tableName,
        rows.map(row => row[idType]),
        isInnerTable,
        (data: ResourceEventData) => editRow(Number(data.resourceId)),
        (data: ResourceEventData) => handleDeletion(Number(data.resourceId))
    );

    const { toMd } = useMediaQueries();
    const { scrollHorizontal, handleScrollHorizontal } = useScroll();

    const renderTableData = () => {
        if (rows.length === 0) {
            return isTableLoading ? <TableLoader /> : <TableEmpty />;
        }

        return (
            <div
                ref={isInnerTable ? null : tableSectionRef}
                className="remaining-height"
                data-testid="table"
            >
                <div
                    className="remaining-height"
                    css={css({
                        position: "relative",
                        overflow: "hidden"
                    })}
                >
                    {isTableLoading && (
                        <div
                            css={css({
                                display: "flex",
                                justifyContent: "center",
                                position: "absolute",
                                top: "88px",
                                width: "100%",
                                zIndex: "4",
                                color: blue700
                            })}
                        >
                            <CircularProgress color="inherit" />
                        </div>
                    )}

                    <div
                        onScroll={onScroll ? handleScrollHorizontal : noop}
                        css={css({
                            position: "absolute",
                            top: "0",
                            left: "0",
                            right: "0",
                            bottom: "0",
                            overflow: "auto",

                            "&:active": {
                                pointerEvents: isTableLoading ? "none" : "auto"
                            }
                        })}
                    >
                        {toMd ? (
                            <TableMobile />
                        ) : (
                            <TableDesktop scrollHorizontal={scrollHorizontal} />
                        )}
                    </div>
                </div>

                {hasDetails && (
                    <TableDetails
                        heights={detailsHeights}
                        setDefaultHeigth={setDefaultHeigth}
                    />
                )}
            </div>
        );
    };

    return (
        <section
            className="remaining-height"
            css={css({
                margin: isInnerTable ? "0 -24px" : "0",
                ...customStyle
            })}
        >
            {!hasNoToolbar && (
                <div>
                    <Toolbar />
                </div>
            )}

            {renderTableData()}
        </section>
    );
};

export default Table;
